import { memo } from 'react';
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    LabelList,
    Pie,
    PieChart,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';
import { getStatusChartHex } from '../../../utils/statusColors';
import { formatMoneyDollars } from '../../../utils/money';
import {
    AXIS_TICK,
    CHART_RESIZE_DEBOUNCE_MS,
    HOVER_CURSOR,
    fmtMonth
} from '../../../utils/chartFormatting';
import type {
    OverviewAgingBucket,
    OverviewCategorySlice,
    OverviewCollectionPoint,
    OverviewMonthlyPoint
} from '../../../services/billing';

// Recharts wrappers for the billing overview. Status hues mirror the
// StatusBadge pills (emerald = paid, teal = open, rose = overdue, gray =
// void); category hues come from the shared hash palette so the donut
// matches the CategoryChip pills on the products page.

export const STATUS_CHART_COLORS = {
    paid: '#059669', // emerald-600
    open: '#0d9488', // teal-600
    overdue: '#e11d48', // rose-600
    void: '#9ca3af' // gray-400
} as const;

const AGING_COLORS: Record<OverviewAgingBucket['bucket'], string> = {
    current: '#0d9488', // teal-600
    '1-30': '#f59e0b', // amber-500
    '31-60': '#d97706', // amber-600
    '61-90': '#e11d48', // rose-600
    '90+': '#9f1239' // rose-800
};

// Bars need at least this much width before an on-chart label is readable.
const MIN_LABEL_BAR_WIDTH = 20;
const BAR_RADIUS = 4;

// Stack segments bottom to top; a segment gets rounded corners only when
// every segment above it in the stack is zero (it is the visible top).
const STACK_ORDER = ['Paid', 'Open', 'Overdue', 'Void'] as const;
type StackKey = (typeof STACK_ORDER)[number];

function topRoundedBarShape(key: StackKey) {
    return (props: unknown): React.ReactElement => {
        const { x, y, width, height, fill, payload } = props as {
            x?: number;
            y?: number;
            width?: number;
            height?: number;
            fill?: string;
            payload?: Record<string, number>;
        };
        if (x === undefined || y === undefined || !width || !height || height <= 0) return <g />;
        const above = STACK_ORDER.slice(STACK_ORDER.indexOf(key) + 1);
        const isTop = above.every((k) => !(payload?.[k] ?? 0));
        if (!isTop) return <rect x={x} y={y} width={width} height={height} fill={fill} />;
        const r = Math.min(BAR_RADIUS, height, width / 2);
        const d = [
            `M${x},${y + height}`,
            `L${x},${y + r}`,
            `Q${x},${y} ${x + r},${y}`,
            `L${x + width - r},${y}`,
            `Q${x + width},${y} ${x + width},${y + r}`,
            `L${x + width},${y + height}`,
            'Z'
        ].join(' ');
        return <path d={d} fill={fill} />;
    };
}

// 12345 -> $12.3k, 2225176 -> $2.2M; for on-chart labels and axis ticks.
function moneyCompact(value: number): string {
    if (Math.abs(value) >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
    if (Math.abs(value) >= 1000) return `$${Math.round(value / 100) / 10}k`;
    return `$${Math.round(value)}`;
}

// ── Shared tooltip, styled like the app's cards ──────────────────────────

interface TooltipEntry {
    name?: string | number;
    value?: number | string;
    color?: string;
    fill?: string;
}

interface ChartTooltipProps {
    active?: boolean;
    label?: string | number;
    payload?: TooltipEntry[];
    formatValue?: (value: number) => string;
    showTotal?: boolean;
}

function ChartTooltip({
    active,
    label,
    payload,
    formatValue = formatMoneyDollars,
    showTotal
}: ChartTooltipProps) {
    if (!active || !payload || payload.length === 0) return null;
    const rows = payload.filter((entry) => Number(entry.value) !== 0 || payload.length === 1);
    const total = payload.reduce((sum, entry) => sum + Number(entry.value ?? 0), 0);
    return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3.5 py-2.5 min-w-[150px]">
            {label !== undefined && (
                <p className="text-xs font-bold text-gray-800 mb-1.5">{label}</p>
            )}
            <div className="flex flex-col gap-1">
                {rows.map((entry, i) => (
                    <div key={i} className="flex items-center gap-2">
                        <span
                            className="w-2 h-2 rounded-[2px] shrink-0"
                            style={{ background: entry.color ?? entry.fill ?? '#9ca3af' }}
                        />
                        <span className="text-[11px] text-gray-500 flex-1">{entry.name}</span>
                        <span className="text-[11px] font-semibold text-gray-900 tabular-nums">
                            {formatValue(Number(entry.value ?? 0))}
                        </span>
                    </div>
                ))}
            </div>
            {showTotal && rows.length > 1 && (
                <div className="flex items-center justify-between gap-2 border-t border-gray-100 mt-1.5 pt-1.5">
                    <span className="text-[11px] font-semibold text-gray-600">Total</span>
                    <span className="text-[11px] font-bold text-gray-900 tabular-nums">
                        {formatValue(total)}
                    </span>
                </div>
            )}
        </div>
    );
}

export function ChartLegend({ items }: { items: { label: string; color: string }[] }) {
    return (
        <div className="flex items-center gap-3">
            {items.map((item) => (
                <span
                    key={item.label}
                    className="flex items-center gap-1.5 text-[11px] text-gray-500"
                >
                    <span
                        className="w-2.5 h-2.5 rounded-[3px]"
                        style={{ background: item.color }}
                    />
                    {item.label}
                </span>
            ))}
        </div>
    );
}

// ── Monthly stacked columns with a total above each bar ─────────────────

export const MonthlyStackedChart = memo(function MonthlyStackedChart({
    data
}: {
    data: OverviewMonthlyPoint[];
}) {
    const rows = data.map((point) => ({
        month: fmtMonth(point.month),
        Paid: Number(point.paid),
        Open: Number(point.open),
        Overdue: Number(point.overdue),
        Void: Number(point.void)
    }));
    const totals = rows.map((row) => row.Paid + row.Open + row.Overdue + row.Void);

    // Rendered on the top-most stack segment: the bar's grand total. Geometry
    // may arrive flat or under viewBox depending on the recharts label pass.
    const renderTotal = (props: unknown): React.ReactElement | string => {
        const p = props as {
            x?: number;
            y?: number;
            width?: number;
            index?: number;
            viewBox?: { x?: number; y?: number; width?: number };
        };
        const x = p.x ?? p.viewBox?.x;
        const y = p.y ?? p.viewBox?.y;
        const width = p.width ?? p.viewBox?.width;
        if (x === undefined || y === undefined || width === undefined || p.index === undefined)
            return '';
        const total = totals[p.index];
        if (!total || width < MIN_LABEL_BAR_WIDTH) return '';
        return (
            <text
                x={x + width / 2}
                y={y - 6}
                textAnchor="middle"
                fontSize={10}
                fontWeight={600}
                fill="#6b7280"
            >
                {moneyCompact(total)}
            </text>
        );
    };

    return (
        <ResponsiveContainer
            width="100%"
            height={240}
            debounce={CHART_RESIZE_DEBOUNCE_MS}
            className="overflow-x-clip"
        >
            <BarChart
                data={rows}
                margin={{ top: 18, right: 4, left: -8, bottom: 0 }}
                barCategoryGap="24%"
            >
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                <XAxis
                    dataKey="month"
                    tick={AXIS_TICK}
                    axisLine={false}
                    tickLine={false}
                    tickMargin={6}
                />
                <YAxis
                    tick={AXIS_TICK}
                    tickFormatter={moneyCompact}
                    axisLine={false}
                    tickLine={false}
                />
                <Tooltip cursor={HOVER_CURSOR} content={<ChartTooltip showTotal />} />
                <Bar
                    dataKey="Paid"
                    stackId="a"
                    fill={STATUS_CHART_COLORS.paid}
                    shape={topRoundedBarShape('Paid')}
                    maxBarSize={52}
                    isAnimationActive={false}
                />
                <Bar
                    dataKey="Open"
                    stackId="a"
                    fill={STATUS_CHART_COLORS.open}
                    shape={topRoundedBarShape('Open')}
                    maxBarSize={52}
                    isAnimationActive={false}
                />
                <Bar
                    dataKey="Overdue"
                    stackId="a"
                    fill={STATUS_CHART_COLORS.overdue}
                    shape={topRoundedBarShape('Overdue')}
                    maxBarSize={52}
                    isAnimationActive={false}
                />
                <Bar
                    dataKey="Void"
                    stackId="a"
                    fill={STATUS_CHART_COLORS.void}
                    shape={topRoundedBarShape('Void')}
                    maxBarSize={52}
                    isAnimationActive={false}
                >
                    <LabelList content={renderTotal} />
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    );
});

// ── Collection rate area with average reference line ────────────────────

export const CollectionRateChart = memo(function CollectionRateChart({
    data,
    avg
}: {
    data: OverviewCollectionPoint[];
    avg: number | null;
}) {
    const rows = data.map((point) => ({
        month: fmtMonth(point.month),
        rate: point.ratePct
    }));
    return (
        <ResponsiveContainer
            width="100%"
            height={180}
            debounce={CHART_RESIZE_DEBOUNCE_MS}
            className="overflow-x-clip"
        >
            <AreaChart data={rows} margin={{ top: 12, right: 4, left: -20, bottom: 0 }}>
                <defs>
                    <linearGradient id="collectionFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={STATUS_CHART_COLORS.paid} stopOpacity={0.22} />
                        <stop
                            offset="100%"
                            stopColor={STATUS_CHART_COLORS.paid}
                            stopOpacity={0.02}
                        />
                    </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                <XAxis
                    dataKey="month"
                    tick={AXIS_TICK}
                    axisLine={false}
                    tickLine={false}
                    tickMargin={6}
                />
                <YAxis
                    domain={[0, 100]}
                    tick={AXIS_TICK}
                    tickFormatter={(v: number) => `${v}%`}
                    axisLine={false}
                    tickLine={false}
                />
                <Tooltip content={<ChartTooltip formatValue={(v) => `${v}%`} />} />
                {avg !== null && (
                    <ReferenceLine
                        y={avg}
                        stroke="#9ca3af"
                        strokeDasharray="4 4"
                        label={{
                            value: `avg ${avg}%`,
                            position: 'insideTopRight',
                            fontSize: 10,
                            fill: '#9ca3af'
                        }}
                    />
                )}
                <Area
                    type="monotone"
                    dataKey="rate"
                    name="Collection rate"
                    stroke={STATUS_CHART_COLORS.paid}
                    strokeWidth={2}
                    fill="url(#collectionFill)"
                    connectNulls
                    dot={{ r: 2.5, fill: STATUS_CHART_COLORS.paid, strokeWidth: 0 }}
                    activeDot={{ r: 4 }}
                    isAnimationActive={false}
                />
            </AreaChart>
        </ResponsiveContainer>
    );
});

// ── Amount collected per month ───────────────────────────────────────────

export const MonthlyCollectedChart = memo(function MonthlyCollectedChart({
    data
}: {
    data: OverviewCollectionPoint[];
}) {
    const rows = data.map((point) => ({
        month: fmtMonth(point.month),
        amount: Number(point.collected)
    }));
    return (
        <ResponsiveContainer
            width="100%"
            height={180}
            debounce={CHART_RESIZE_DEBOUNCE_MS}
            className="overflow-x-clip"
        >
            <BarChart
                data={rows}
                margin={{ top: 18, right: 4, left: -8, bottom: 0 }}
                barCategoryGap="24%"
            >
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                <XAxis
                    dataKey="month"
                    tick={AXIS_TICK}
                    axisLine={false}
                    tickLine={false}
                    tickMargin={6}
                />
                <YAxis
                    tick={AXIS_TICK}
                    tickFormatter={moneyCompact}
                    axisLine={false}
                    tickLine={false}
                />
                <Tooltip cursor={HOVER_CURSOR} content={<ChartTooltip />} />
                <Bar
                    dataKey="amount"
                    name="Collected"
                    fill={STATUS_CHART_COLORS.paid}
                    radius={[3, 3, 0, 0]}
                    maxBarSize={52}
                    isAnimationActive={false}
                >
                    <LabelList
                        dataKey="amount"
                        position="top"
                        formatter={(value) =>
                            Number(value) > 0 ? moneyCompact(Number(value)) : ''
                        }
                        style={{ fontSize: 10, fontWeight: 600, fill: '#6b7280' }}
                    />
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    );
});

// ── A/R aging bars with amounts above ────────────────────────────────────

export const AgingChart = memo(function AgingChart({ data }: { data: OverviewAgingBucket[] }) {
    const rows = data.map((bucket) => ({
        bucket: bucket.bucket,
        amount: Number(bucket.amount),
        count: bucket.count
    }));
    return (
        <ResponsiveContainer
            width="100%"
            height={170}
            debounce={CHART_RESIZE_DEBOUNCE_MS}
            className="overflow-x-clip"
        >
            <BarChart
                data={rows}
                margin={{ top: 18, right: 4, left: -8, bottom: 0 }}
                barCategoryGap="24%"
            >
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                <XAxis
                    dataKey="bucket"
                    tick={AXIS_TICK}
                    axisLine={false}
                    tickLine={false}
                    tickMargin={6}
                />
                <YAxis
                    tick={AXIS_TICK}
                    tickFormatter={moneyCompact}
                    axisLine={false}
                    tickLine={false}
                />
                <Tooltip
                    cursor={HOVER_CURSOR}
                    content={<ChartTooltip />}
                    labelFormatter={(label) =>
                        label === 'current' ? 'Not yet due' : `${label} days overdue`
                    }
                />
                <Bar
                    dataKey="amount"
                    name="Outstanding"
                    radius={[3, 3, 0, 0]}
                    maxBarSize={52}
                    isAnimationActive={false}
                >
                    {rows.map((row) => (
                        <Cell
                            key={row.bucket}
                            fill={AGING_COLORS[row.bucket as OverviewAgingBucket['bucket']]}
                        />
                    ))}
                    <LabelList
                        dataKey="amount"
                        position="top"
                        formatter={(value) =>
                            Number(value) > 0 ? moneyCompact(Number(value)) : ''
                        }
                        style={{ fontSize: 10, fontWeight: 600, fill: '#6b7280' }}
                    />
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    );
});

// ── Category donut with the range total in the center ───────────────────

export const CategoryDonut = memo(function CategoryDonut({
    data
}: {
    data: OverviewCategorySlice[];
}) {
    const rows = data.map((slice) => ({
        name: slice.category,
        value: Number(slice.amount),
        color: slice.category === 'Other' ? '#9ca3af' : getStatusChartHex(slice.category)
    }));
    if (rows.length === 0) {
        return (
            <p className="text-sm text-gray-400 py-10 text-center">
                No invoiced revenue in this range.
            </p>
        );
    }
    const total = rows.reduce((sum, row) => sum + row.value, 0);
    return (
        <div className="flex items-center gap-4">
            <div className="relative shrink-0">
                <PieChart width={150} height={150}>
                    <Pie
                        data={rows}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={48}
                        outerRadius={70}
                        paddingAngle={2}
                        strokeWidth={0}
                        isAnimationActive={false}
                    >
                        {rows.map((row) => (
                            <Cell key={row.name} fill={row.color} />
                        ))}
                    </Pie>
                    <Tooltip content={<ChartTooltip />} />
                </PieChart>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <span className="text-sm font-bold text-gray-900 tabular-nums">
                        {moneyCompact(total)}
                    </span>
                    <span className="text-[10px] text-gray-400">invoiced</span>
                </div>
            </div>
            <div className="flex flex-col gap-2 flex-1 min-w-0">
                {rows.map((row) => (
                    <div key={row.name} className="flex items-center gap-2">
                        <span
                            className="w-2.5 h-2.5 rounded-[3px] shrink-0"
                            style={{ background: row.color }}
                        />
                        <span className="text-[13px] text-gray-600 flex-1 truncate">
                            {row.name}
                        </span>
                        <span className="text-[13px] font-semibold text-gray-900 tabular-nums">
                            {formatMoneyDollars(row.value)}
                        </span>
                        <span className="text-[11px] text-gray-400 tabular-nums w-9 text-right">
                            {total > 0 ? Math.round((row.value / total) * 100) : 0}%
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
});
